import React, { createContext, useState, useContext, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { filterMenuItems } from '../../helper/searchHelper'
import menuItems from '../../components/sidebar/menuData'

// Create the context
const SidebarContext = createContext()

// Create a custom hook to use this context
export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

// Provider component
export const SidebarProvider = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024)
  const [isSidebarLoaded, setIsSidebarLoaded] = useState(false)
  const [activeMenu, setActiveMenu] = useState(null)

  // Menu state management
  const [activeItems, setActiveItems] = useState(new Set())
  const [activeSubItems, setActiveSubItems] = useState(new Set())

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchMatchPaths, setSearchMatchPaths] = useState([])

  // Toggle sidebar visibility
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev)
  }, [])

  // Toggle a main menu item
  const toggleMenuItem = useCallback(
    itemId => {
      if (searchTerm === '') {
        setActiveItems(prev => {
          const newActive = new Set()
          if (!prev.has(itemId)) {
            newActive.add(itemId)
          }
          return newActive
        })
        setActiveSubItems(new Set())
      }
    },
    [searchTerm]
  )

  // Toggle a submenu item
  const toggleSubMenuItem = useCallback(
    (e, subItemId, parentId) => {
      e.stopPropagation()
      if (searchTerm === '') {
        setActiveItems(prev => {
          const newActive = new Set(prev)
          newActive.add(parentId)
          return newActive
        })

        setActiveSubItems(prev => {
          const newActive = new Set()
          if (!prev.has(subItemId)) {
            newActive.add(subItemId)
          }
          return newActive
        })
      }
    },
    [searchTerm]
  )

  // Handle search
  const handleSearch = useCallback(term => {
    setSearchTerm(term)

    if (term === '') {
      setSearchResults([])
      setSearchMatchPaths([])
      return
    }

    const filtered = filterMenuItems(menuItems, term)
    setSearchResults(filtered)
    const paths = new Set()
    const findMatchesAndPaths = (items, parentPath = []) => {
      items.forEach(item => {
        const itemMatches = item.label.toLowerCase().includes(term.toLowerCase())

        if (itemMatches) {
          paths.add(item.id)
          parentPath.forEach(pid => paths.add(pid))
        }

        if (item.subItems && item.subItems.length > 0) {
          const newPath = [...parentPath, item.id]
          findMatchesAndPaths(item.subItems, newPath)
        }
      })
    }

    findMatchesAndPaths(menuItems)
    setSearchMatchPaths(Array.from(paths))
  }, [])

  const clearSearch = useCallback(() => {
    setSearchTerm('')
    setSearchResults([])
    setSearchMatchPaths([])
  }, [])

  useEffect(() => {
    if (searchTerm === '') {
    } else {
      setActiveItems(new Set(searchMatchPaths))
      setActiveSubItems(new Set())
    }
  }, [searchTerm, searchMatchPaths])

  useEffect(() => {
    const initializeSidebar = () => {
      setIsSidebarOpen(window.innerWidth >= 1024)
      setIsSidebarLoaded(true)
    }

    initializeSidebar()
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Check if an item should be expanded (either by user toggle or search match)
  const isItemExpanded = useCallback(
    itemId => {
      return (searchTerm !== '' && searchMatchPaths.includes(itemId)) || activeItems.has(itemId)
    },
    [searchTerm, searchMatchPaths, activeItems]
  )

  // Check if a subitem should be expanded
  const isSubItemExpanded = useCallback(
    itemId => {
      return (searchTerm !== '' && searchMatchPaths.includes(itemId)) || activeSubItems.has(itemId)
    },
    [searchTerm, searchMatchPaths, activeSubItems]
  )

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false)
  }, [])

  return (
    <SidebarContext.Provider
      value={{
        isSidebarOpen,
        toggleSidebar,
        // closeSidebar,
        activeMenu,
        setActiveMenu,
        isSidebarLoaded,
        activeItems,
        activeSubItems,
        searchTerm,
        searchResults,
        searchMatchPaths,
        isItemExpanded,
        isSubItemExpanded,
        toggleMenuItem,
        toggleSubMenuItem,
        handleSearch,
        clearSearch,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

// PropTypes validation
SidebarProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default SidebarContext
